import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "redux-store/hooks";

import { useSnackbar } from "notistack";

import { RenderCellExpand } from "shared-components/expanded-cel";
import PaginationFooter from "shared-components/pagination";
import TableData from "shared-components/table-data";

import DeleteUser from "pages/user-profile/components/delete-user";
import { DeletePopupModal } from "pages/best-deals/components/deals-table/styles";
import { RemoveIconLogo } from "pages/user-profile/components/user-management/styles";

import { useHistory } from "react-router-dom";

import { limit } from "constants/pagination-constants";
import { loadingHelper } from "constants/loadingHelper";

import {
  GridCellParams,
  GridColDef,
  GridRowModel,
  GridRowParams,
} from "@material-ui/data-grid";

import {
  setCurrentPageReducer,
  deleteNotificationThunk,
  getAllNotificationsThunk,
} from "slices/notifications";
import { NOTIFICATION_DETAILS, COPY_NOTIFICATION } from "constants/route/route-constants";
import { CopyIconLogo, DispatchStatusText } from "pages/management/notification/notification-table/styles";

interface Props {
  selectedLang: string;
}

const NotificationTable = ({selectedLang}:Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const [loading, setLoading] = useState<boolean>(false);
  const [paginationSize, setPaginationSize] = useState<number>(0);
  const [rows, setRows] = useState<GridRowModel>([]);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [selectedDeleteID, setSelectedDeletedId] = useState<number>(0);

  const notifications = useAppSelector(
    (state) => state.notifications.notificationsInfo
  );
  const brandName = useAppSelector((state) => {
    return state.brand.brandType;
  });

  useEffect(() => {
    loadingHelper(
      dispatch(getAllNotificationsThunk({ pageno: 1, limit: limit })),
      setLoading,
      enqueueSnackbar
    );
    dispatch(setCurrentPageReducer(1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, brandName]);

  useEffect(() => {
    setRows(notifications?.allNotifications);
    setPaginationSize(Math.ceil(notifications?.recordsLength / limit));
  }, [notifications]);

  const openDeleteDealModal = (id: number) => {
    setOpenDeleteModal(true);
    setSelectedDeletedId(id);
  };

  const closeDeleteModal = () => {
    setOpenDeleteModal(false);
  };

  const confirmDelete = async (id: number) => {
    try {
      setLoading(true);
      const result = await dispatch(deleteNotificationThunk(id)).unwrap();
      enqueueSnackbar(
        result?.data?.status || result?.data || "Notification deleted successfully",
        {
          variant: "success",
        }
      );
      dispatch(
        getAllNotificationsThunk({
          pageno: notifications?.currentPage || 1,
          limit: limit,
        })
      );
      setLoading(false);
      setOpenDeleteModal(false);
    } catch (error: any) {
      setLoading(false);
      enqueueSnackbar(error?.data || "Unable to delete notification", {
        variant: "error",
      });
      setOpenDeleteModal(false);
    }
  };

  const handlePagination = (event, number) => {
      // dispatch(getAllNotificationsThunk({ pageno: number, limit: limit}));
      loadingHelper(
        dispatch(
          getAllNotificationsThunk({
            pageno: number,
            limit:limit,
          })
        ),
        setLoading,
        enqueueSnackbar
      );
    dispatch(setCurrentPageReducer(number)); //
  };
const handelDateFormat = (timedate) => {
  const day = new Date(timedate);
  const time = day.toLocaleString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
  var dateTime =
    day.getDate() +
    "-" +
    (day.getMonth() + 1) +
    "-" +
    day.getFullYear() +
    " " +
    time;

  return dateTime;
};

  const handleRowClick = (params: GridRowParams) => {
    history.push({
      pathname: NOTIFICATION_DETAILS,
      state: {
        detail: "view",
        rowData: params.row,
      },
    });
  };

  const columns: GridColDef[] = [
    {
      field: "createdOn",
      headerName: "Date",
      sortable: false,
      flex: 0.33,
      renderCell: (params: GridCellParams) => {
        const { createdOn } = params.row;
        return  handelDateFormat(createdOn) ;
      },
    },
    {
      field: "role",
      headerName: "User Type",
      sortable: false,
      resizable: false,
      flex: 0.16,
      renderCell:  RenderCellExpand
    },
    {
      field: "notificationCategory",
      headerName: "Category",
      sortable: false,
      resizable: false,
      flex: 0.4,
      renderCell: (params: GridCellParams) => {
        return RenderCellExpand(params, "notificationCategory", 400);
      },
    },
    {
      field: `${selectedLang === "English" ? "title" : "titleArabic"}`,
      sortable: false,
      headerName: "Header Text Key",
      resizable: false,
      flex: 0.45,
      renderCell: (params: GridCellParams) => {
        return RenderCellExpand(params, "notificationTitle", 400, selectedLang);
      },
    },
    {
      field: `${selectedLang === "English" ? "body" : "bodyArabic"}`,
      headerName: "Sub Text key",
      sortable: false,
      resizable: false,
      flex: 0.45,
      renderCell: (params: GridCellParams) => {
        return RenderCellExpand(params, "notificationBody", 400, selectedLang);
      },
    },
    {
      field: "ActionType",
      headerName: "Action",
      sortable: false,
      resizable: false,
      flex: 0.18,
      renderCell: (params: GridCellParams) => {
        const type = params.row?.extraInfo?.action_type;
        return <>{type != null && String(type).trim() !== "" ? type : "N/A"}</>;
      },
    },
    {
      field: "Label",
      headerName: "Label",
      sortable: false,
      resizable: false,
      flex: 0.3,
      renderCell: (params: GridCellParams) => {
        return RenderCellExpand(params, "notificationLabel", 400);
      },
    },
    {
      field: "dispatchStatus",
      headerName: "Status",
      sortable: false,
      resizable: false,
      flex: 0.20,
      renderCell: (params: GridCellParams) => {
        const status = params.row?.dispatchStatus;
        const text =
          status != null && String(status).trim() !== ""
            ? String(status).trim()
            : "N/A";
        const normalizedStatus = text.toLowerCase();
        return (
          <DispatchStatusText $status={normalizedStatus}>
            {text}
          </DispatchStatusText>
        );
      },
    },
    {
      field: "id",
      sortable: false,
      headerName: "Actions",
      resizable: false,
      flex: 0.25,
      renderCell: (params: GridCellParams) => {
        const rowData = params.row;
        return (
          <>
            <CopyIconLogo
              onClick={(e) => {
                e.stopPropagation();
                history.push({
                  pathname: COPY_NOTIFICATION,
                  state: {
                    detail: "copy",
                    rowId: rowData?.id,
                    rowData,
                  },
                });
              }}
            />
            <RemoveIconLogo
              onClick={(e) => {
                e.stopPropagation();
                openDeleteDealModal(rowData?.id);
              }}
            />
          </>
        );
      },
    },
  ];

  return (
    <>
      <TableData
        rows={loading ? [] : (rows as GridRowModel[])}
        isLoading={loading}
        columns={columns}
        isCheckBox={false}
        pageSize={limit}
        isPagination={true}
        onRowClick={handleRowClick}
      />
      {paginationSize > 1 ? (
        <PaginationFooter
          handleChange={(event, value) => {
            handlePagination(event, value);
          }}
          dataSize={paginationSize}
          pageNumber={notifications?.currentPage}
        />
      ) : null}

      <DeletePopupModal open={openDeleteModal} onClose={closeDeleteModal}>
        <DeleteUser
          params={selectedDeleteID}
          closeModal={closeDeleteModal}
          confirmDelete={confirmDelete}
          loading={loading}
          headerText={"Delete Notification"}
          paraText={"Are you sure you want to delete this Notification?"}
          message={"All data related to this Notification will be deleted."}
        ></DeleteUser>
      </DeletePopupModal>
    </>
  );
};

export default NotificationTable;
